import * as React from 'react';
import {
  Box,
  Modal,
  StackLayout,
  HeadingText,
  UtilityText,
} from '@leagueplatform/genesis-core';

interface ImagePreviewModalProps {
  open: boolean;
  onClose: () => void;
  mediaUrl?: string;
  title?: string;
  description?: string;
}

/**
 * ImagePreviewModal component displays a modal with an image preview.
 * @param open - Whether the modal is open
 * @param onClose - Callback function to close the modal
 * @param mediaUrl - URL of the media to preview
 * @param title - Title of the media
 * @param description - Description of the media
 * @returns MediaPreviewModal component
 */
export const ImagePreviewModal: React.FC<ImagePreviewModalProps> = ({
  open,
  onClose,
  mediaUrl,
  title,
  description,
}) => (
  <Modal.Root open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
    <Modal.Content
      css={{
        '.GDS-modal': {
          minWidth: '50%',
          maxWidth: '100%',
        },
      }}
    >
      <StackLayout
        orientation="vertical"
        spacing="$none"
        horizontalAlignment="stretch"
      >
        {/* Media container */}
        <Box
          css={{
            backgroundColor: '$surfaceBackgroundTertiary',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            overflow: 'hidden',
            minHeight: '200px',
          }}
        >
          <Box
            as="img"
            src={mediaUrl}
            alt={title || 'Image preview'}
            css={{
              width: 'fit-content',
              height: 'fit-content',
              objectFit: 'contain',
              maxWidth: '100%',
              maxHeight: 'calc(100vh - 300px)',
            }}
          />
        </Box>

        {/* Title and description */}
        {(title || description) && (
          <StackLayout
            spacing="$one"
            horizontalAlignment="stretch"
            css={{ paddingBlockStart: '$two' }}
          >
            {title && (
              <Modal.Title>
                <HeadingText size="md" level="1">
                  {title}
                </HeadingText>
              </Modal.Title>
            )}
            {description && (
              <Modal.Description>
                <UtilityText>{description}</UtilityText>
              </Modal.Description>
            )}
          </StackLayout>
        )}
      </StackLayout>
    </Modal.Content>
  </Modal.Root>
);
